import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import styles from './ProjectDocumentationNew.module.scss';
import BotoneraModern from '../../components/Botonera/BotoneraModern.tsx';
import useNavVisibility from '../../hooks/useNavVisibility.ts';
import CreationProgressModal from '../../components/UI/CreationProgressModal/CreationProgressModal.tsx';
import {
  fetchCategories,
  fetchUses,
  fetchMaterialities,
  fetchFileTags,
  fetchSubCategories,
  setSelectedType,
  setSelectedCategory,
  setSelectedSubCategory,
  setSelectedUses,
  setSelectedMaterialities,
  setFormData,
  replaceFormData,
  updateProjectDocumentation,
  fetchProjectDocumentation
} from '../../store/actions/projectDocumentationActions';
import { openMainModal, closeMainModal } from '../../store/actions';
import { ProjectDocumentationForm, FileItem } from '../../types/projectDocumentation';
import { buildPartialPayload, getFileChanges, AdditionalData } from '../../utils/projectDocumentationBuilder.ts';
import { buildProfileUrl } from '../../utils/navigationHelpers.ts';
import { validateProjectDocumentationForm } from '../../utils/projectDocumentationValidator.ts';
import { projectDocumentationService } from '../../services/projectDocumentationService.ts';
import ProjectForm from './components/ProjectForm.tsx';
import CustomSelect from './components/CustomSelect.tsx';
import Breadcrumbs from '../../components/UI/Breadcrumbs/Breadcrumbs.tsx';

interface Props {
  categories: any[];
  subCategories: any[];
  uses: any[];
  materialities: any[];
  fileTags: any[];
  selectedType: 'proyecto' | 'anteproyecto' | null;
  selectedCategory: string | null;
  selectedSubCategory: string | null;
  selectedUses: string[];
  selectedMaterialities: string[];
  formData: ProjectDocumentationForm;
  loading: boolean;
  updateLoading: boolean;
  userData: any;
  fetchCategories: () => void;
  fetchUses: () => void;
  fetchMaterialities: () => void;
  fetchFileTags: () => void;
  fetchSubCategories: (categoryId: string) => void;
  setSelectedType: (type: 'proyecto' | 'anteproyecto') => void;
  setSelectedCategory: (categoryId: string) => void;
  setSelectedSubCategory: (subCategoryId: string) => void;
  setSelectedUses: (useIds: string[]) => void;
  setSelectedMaterialities: (materialityIds: string[]) => void;
  setFormData: (formData: Partial<ProjectDocumentationForm>) => void;
  replaceFormData: (formData: ProjectDocumentationForm) => void;
  updateProjectDocumentation: (id: string, payload: any, items?: any[]) => Promise<any>;
  fetchProjectDocumentation: (id: string) => Promise<any>;
  openMainModal: (config: any) => void;
  closeMainModal: () => void;
}

const ProjectDocumentationEdit: React.FC<Props> = ({
  categories,
  subCategories,
  uses,
  materialities,
  fileTags,
  selectedType,
  selectedCategory,
  selectedSubCategory,
  selectedUses,
  selectedMaterialities,
  formData,
  loading,
  updateLoading,
  userData,
  fetchCategories,
  fetchUses,
  fetchMaterialities,
  fetchFileTags,
  fetchSubCategories,
  setSelectedType,
  setSelectedCategory,
  setSelectedSubCategory,
  setSelectedUses,
  setSelectedMaterialities,
  setFormData,
  replaceFormData,
  updateProjectDocumentation,
  fetchProjectDocumentation,
  openMainModal,
  closeMainModal
}) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const [modalState, setModalState] = useState<'uploading' | 'success' | 'pending' | 'error' | null>(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [initialFileItems, setInitialFileItems] = useState<FileItem[]>([]);
  const [failedItems, setFailedItems] = useState<FileItem[]>([]);
  const [originalFormData, setOriginalFormData] = useState<ProjectDocumentationForm | null>(null);
  const [originalAdditionalData, setOriginalAdditionalData] = useState<AdditionalData | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        await Promise.all([
          fetchCategories(),
          fetchUses(),
          fetchMaterialities(),
          fetchFileTags()
        ]);

        if (id) {
          const response = await fetchProjectDocumentation(id);
          const project = response?.data || response || {};

          // Verificar que el usuario es el dueño del proyecto
          const projectUserId = project.user?.id || project.user_id;
          const currentUserId = userData?.id;
          
          if (projectUserId && currentUserId && Number(projectUserId) !== Number(currentUserId)) {
            // El usuario no es el dueño, mostrar error y redirigir
            openMainModal({
              title: 'Acceso denegado',
              body: 'No tienes permisos para editar esta documentación de obra. Solo el creador puede editarla.',
              iconType: 'error',
              isSuccess: true,
              confirmHandler: null,
              customCloseMainModal: () => {
                navigate('/dashboard');
                closeMainModal();
              },
            });
            return;
          }

          replaceFormData({
            name: project.name || '',
            description: project.description || '',
            total_surface: Number(project.total_surface || 0),
            covered_surface: Number(project.covered_surface || 0),
            semicovered_surface: Number(project.semicovered_surface || 0),
            uncovered_surface: Number(project.uncovered_surface || 0),
            environments: Number(project.environments || 0),
            bedrooms: Number(project.bedrooms || 0),
            bathrooms: Number(project.bathrooms || 0),
            floors: Number(project.floors || 0),
            garage: Boolean(project.garage),
            yard: Boolean(project.yard),
            terrace: Boolean(project.terrace),
            pool: Boolean(project.pool),
            implantation: Number(project.implantation || 0),
            construction_type: project.construction_type || 'tradicional',
            stage: project.stage || 'anteproyecto',
            state: project.state || 'pending',
            budget: Number(project.budget || 0),
            budget_currency: project.budget_currency || 'ARS',
            consultation_price: Number(project.consultation_price || 0),
            consultation_price_currency: project.consultation_price_currency || 'ARS',
            price: Number(project.price || project.package_price || 0),
            price_currency: project.price_currency || project.package_currency || 'ARS',
            preliminary_project_subcategory_id: Number(project.subCategory?.id || project.preliminary_project_subcategory_id || 0),
            user_id: Number(project.user?.id || project.user_id || 0),
            materiality_id: Number(project.materiality?.id || project.materiality_id || 0),
            use_id: Number(project.use?.id || project.use_id || 0),
            pause: Boolean(project.pause),
            images: []
          });

          const stageVal = project.stage === 'proyecto' ? 'proyecto' : 'anteproyecto';
          setSelectedType(stageVal);

          const categoryId = String(project.subCategory?.category?.id || '');
          if (categoryId) {
            setSelectedCategory(categoryId);
            await fetchSubCategories(categoryId);
          }

          const subCategoryId = String(project.subCategory?.id || '');
          if (subCategoryId) setSelectedSubCategory(subCategoryId);

          const useId = String(project.use?.id || '');
          if (useId) setSelectedUses([useId]);

          const materialityId = String(project.materiality?.id || '');
          if (materialityId) setSelectedMaterialities([materialityId]);

          const serverFiles = Array.isArray(project.files) ? project.files : [];
          const mapped: FileItem[] = serverFiles.map((f: any) => ({
            id: String(f.id),
            type: 'link',
            link: f.url,
            tagId: String(f.tag?.id || ''),
            name: f.name_file
          }));
          setInitialFileItems(mapped);

          const originalFormDataSnapshot: ProjectDocumentationForm = {
            name: project.name || '',
            description: project.description || '',
            total_surface: Number(project.total_surface || 0),
            covered_surface: Number(project.covered_surface || 0),
            semicovered_surface: Number(project.semicovered_surface || 0),
            uncovered_surface: Number(project.uncovered_surface || 0),
            environments: Number(project.environments || 0),
            bedrooms: Number(project.bedrooms || 0),
            bathrooms: Number(project.bathrooms || 0),
            floors: Number(project.floors || 0),
            garage: Boolean(project.garage),
            yard: Boolean(project.yard),
            terrace: Boolean(project.terrace),
            pool: Boolean(project.pool),
            implantation: Number(project.implantation || 0),
            construction_type: project.construction_type || 'tradicional',
            stage: project.stage || 'anteproyecto',
            state: project.state || 'pending',
            budget: Number(project.budget || 0),
            budget_currency: project.budget_currency || 'ARS',
            consultation_price: Number(project.consultation_price || 0),
            consultation_price_currency: project.consultation_price_currency || 'ARS',
            price: Number(project.price || project.package_price || 0),
            price_currency: project.price_currency || project.package_currency || 'ARS',
            preliminary_project_subcategory_id: Number(project.subCategory?.id || project.preliminary_project_subcategory_id || 0),
            user_id: Number(project.user?.id || project.user_id || 0),
            materiality_id: Number(project.materiality?.id || project.materiality_id || 0),
            use_id: Number(project.use?.id || project.use_id || 0),
            pause: Boolean(project.pause),
            images: []
          };
          setOriginalFormData(originalFormDataSnapshot);

          const originalAdditionalDataSnapshot: AdditionalData = {
            selectedType: stageVal,
            selectedSubCategory: subCategoryId,
            selectedMaterialities: materialityId ? [materialityId] : [],
            selectedUses: useId ? [useId] : []
          };
          setOriginalAdditionalData(originalAdditionalDataSnapshot);

          const navState: any = location.state || {};
          if (navState.uploadFailures) {
            setFailedItems([]);
          }
        }
      } catch (e) {}
    };
    load();
  }, [id, userData?.id, navigate, location.state, openMainModal, closeMainModal, fetchCategories, fetchUses, fetchMaterialities, fetchFileTags, fetchProjectDocumentation, replaceFormData, setSelectedType, setSelectedCategory, fetchSubCategories, setSelectedSubCategory, setSelectedUses, setSelectedMaterialities]);

  useEffect(() => {
    if (fileTags.length > 0 && initialFileItems.length > 0 && originalFormData && selectedType) {
      const validation = validateProjectDocumentationForm(
        originalFormData,
        initialFileItems,
        fileTags,
        selectedType
      );
      if (!validation.isValid && validation.errors.requiredDocs) {
        openMainModal({
          title: 'Faltan documentos requeridos',
          body: validation.errors.requiredDocs,
          iconType: 'warning',
          isSuccess: true,
          confirmHandler: null,
        });
      }
    }
  }, [fileTags, initialFileItems, originalFormData, selectedType, openMainModal]);

  const handleSubmit = async (finalFormData: ProjectDocumentationForm, fileItems: FileItem[]) => {
    if (!id || !originalFormData || !originalAdditionalData) return;

    if (!selectedSubCategory || selectedSubCategory === '' || selectedSubCategory === '0') {
      openMainModal({
        title: 'Subcategoría requerida',
        body: 'Debes seleccionar una subcategoría antes de guardar los cambios.',
        iconType: 'warning',
        isSuccess: true,
        confirmHandler: null,
      });
      return;
    }

    try {
      const newAdditionalData: AdditionalData = {
        selectedType: selectedType || 'anteproyecto',
        selectedSubCategory: selectedSubCategory || '',
        selectedMaterialities,
        selectedUses
      };

      const partialPayload = buildPartialPayload(
        originalFormData,
        originalAdditionalData,
        finalFormData,
        newAdditionalData
      );

      const { newFiles, deletedFileIds } = getFileChanges(initialFileItems, fileItems);

      if (Object.keys(partialPayload).length > 0) {
        await updateProjectDocumentation(id, partialPayload, []);
      }

      if (newFiles.length > 0) {
        setModalState('uploading');
        setUploadProgress({ current: 0, total: newFiles.length });
        const onProgress = (current: number, total: number) => {
          setUploadProgress({ current, total });
        };
        await projectDocumentationService.uploadProjectDocumentationFiles(Number(id), newFiles, onProgress);
      }

      if (deletedFileIds.length > 0) {
        await Promise.all(
          deletedFileIds.map(fileId => 
            projectDocumentationService.deleteProjectDocumentationFile(fileId)
          )
        );
      }

      setModalState('success');
    } catch (e) {
      setModalState('error');
    }
  };

  const handleModalSuccess = () => {
    setModalState(null);
    if (userData) {
      const userName = userData.type === "COMPANY" 
        ? userData.razon_social 
        : `${userData.name} ${userData.last_name}`;
      const profileUrl = buildProfileUrl(String(userData.id), userName, 'Gestión', {
        section: 'documentaciones',
      });
      navigate(profileUrl);
    } else {
      navigate('/dashboard');
    }
  };

  const handleModalError = () => {
    setModalState(null);
  };

  return (
    <div className={styles.container}>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      <div className={styles.mainContainer}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <Breadcrumbs />
            <h1 className={styles.title}>Editar <span className={styles.titleHighlight}>{selectedType === 'proyecto' ? 'Proyecto' : 'Anteproyecto'}</span> de Obra</h1>
          </div>
        </div>
        <div className={styles.content}>
          {failedItems.length > 0 && (
            <div className={styles.warningBox}>
              <div className={styles.warningBox_title}>Algunos archivos no se subieron</div>
              <div className={styles.warningBox_message}>Podés reintentar la carga o adjuntarlos como links.</div>
              <button
                className={styles.warningBox_button}
                onClick={async () => {
                  if (!id) return;
                  setModalState('uploading');
                  setUploadProgress({ current: 0, total: failedItems.length });
                  try {
                    const { projectDocumentationService } = await import('../../services/projectDocumentationService.ts');
                    const onProgress = (current: number, total: number) => setUploadProgress({ current, total });
                    const { failures } = await projectDocumentationService.uploadProjectDocumentationFiles(Number(id), failedItems, onProgress);
                    if (failures.length === 0) {
                      setFailedItems([]);
                      setModalState('success');
                    } else {
                      setModalState('error');
                    }
                  } catch (e) {
                    setModalState('error');
                  }
                }}
              >
                Reintentar fallidos
              </button>
            </div>
          )}
          <div className={styles.filtersGrid}>
            <div className={styles.filterField}>
              <div className={styles.filterField_label}>Tipo</div>
              <CustomSelect
              options={[{ value: 'anteproyecto', label: 'Anteproyecto' }, { value: 'proyecto', label: 'Proyecto' }]}
              value={selectedType || ''}
              onChange={(v: string) => {
                setSelectedType(v as any);
                if (formData && fileTags.length > 0 && initialFileItems.length > 0) {
                  const validation = validateProjectDocumentationForm(
                    formData,
                    initialFileItems,
                    fileTags,
                    v as 'proyecto' | 'anteproyecto'
                  );
                  if (!validation.isValid && validation.errors.requiredDocs) {
                    openMainModal({
                      title: 'Faltan documentos requeridos',
                      body: validation.errors.requiredDocs,
                      iconType: 'warning',
                      isSuccess: true,
                      confirmHandler: null,
                    });
                  }
                }
              }}
              placeholder="Tipo"
              />
            </div>
            <div className={styles.filterField}>
              <div className={styles.filterField_label}>Categoría</div>
              <CustomSelect
              options={(categories || []).map((c: any) => ({ value: String(c.id), label: c.name }))}
              value={selectedCategory || ''}
              onChange={(v: string) => { 
                const previousCategory = selectedCategory;
                setSelectedCategory(v);
                fetchSubCategories(v);
                if (v && previousCategory && v !== previousCategory) {
                  openMainModal({
                    title: 'Selecciona una subcategoría',
                    body: 'Al cambiar la categoría, debes seleccionar una nueva subcategoría.',
                    iconType: 'warning',
                    isSuccess: true,
                    confirmHandler: null,
                  });
                }
              }}
              placeholder="Categoría"
              />
            </div>
            <div className={styles.filterField}>
              <div className={styles.filterField_label}>Subcategoría</div>
              <CustomSelect
              options={(subCategories || []).map((s: any) => ({ value: String(s.id), label: s.name }))}
              value={selectedSubCategory || ''}
              onChange={(v: string) => setSelectedSubCategory(v)}
              placeholder="Subcategoría"
              />
            </div>
            <div className={styles.filterField}>
              <div className={styles.filterField_label}>Uso</div>
              <CustomSelect
              options={(uses || []).map((u: any) => ({ value: String(u.id), label: u.name }))}
              value={selectedUses[0] || ''}
              onChange={(v: string) => setSelectedUses([v])}
              placeholder="Uso"
              />
            </div>
            <div className={styles.filterField}>
              <div className={styles.filterField_label}>Materialidad</div>
              <CustomSelect
              options={(materialities || []).map((m: any) => ({ value: String(m.id), label: m.name }))}
              value={selectedMaterialities[0] || ''}
              onChange={(v: string) => setSelectedMaterialities([v])}
              placeholder="Materialidad"
              />
            </div>
          </div>
          <ProjectForm
            formData={formData}
            fileTags={fileTags}
            projectType={selectedType || 'anteproyecto'}
            onSubmit={handleSubmit}
            onBack={() => navigate('/dashboard')}
            loading={updateLoading}
            selectedSubCategory={selectedSubCategory}
            selectedMaterialities={selectedMaterialities}
            selectedUses={selectedUses}
            initialFileItems={initialFileItems}
            hideHeaderText
            isEditMode={true}
          />
        </div>
      </div>

      <CreationProgressModal
        isOpen={modalState !== null}
        state={modalState || 'pending'}
        message={
          modalState === 'uploading'
            ? 'Subiendo documentos...'
            : modalState === 'success'
            ? 'Tu anteproyecto ha sido actualizado exitosamente y está pendiente de revisión.'
            : 'Ocurrió un error al actualizar. Intentá nuevamente.'
        }
        progress={modalState === 'uploading' ? uploadProgress : undefined}
        projectPreviewUrl={undefined}
        onSuccess={handleModalSuccess}
        onError={handleModalError}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  categories: state.anteProyecto.categories,
  subCategories: state.anteProyecto.subCategories,
  uses: state.anteProyecto.uses,
  materialities: state.anteProyecto.materialities,
  fileTags: state.anteProyecto.fileTags,
  selectedType: state.anteProyecto.selectedType,
  selectedCategory: state.anteProyecto.selectedCategory,
  selectedSubCategory: state.anteProyecto.selectedSubCategory,
  selectedUses: state.anteProyecto.selectedUses,
  selectedMaterialities: state.anteProyecto.selectedMaterialities,
  formData: state.anteProyecto.formData,
  loading: state.anteProyecto.loading,
  updateLoading: state.anteProyecto.updateLoading,
  userData: state.general.userData
});

export default connect(mapStateToProps, {
  fetchCategories,
  fetchUses,
  fetchMaterialities,
  fetchFileTags,
  fetchSubCategories,
  setSelectedType,
  setSelectedCategory,
  setSelectedSubCategory,
  setSelectedUses,
  setSelectedMaterialities,
  setFormData,
  replaceFormData,
  updateProjectDocumentation,
  fetchProjectDocumentation,
  openMainModal,
  closeMainModal
})(ProjectDocumentationEdit);


